import { useMemo, useState } from "react";
import type { Address } from "viem";
import type { Pool } from "../hooks/usePools";
import { useOcupadas, type Ocupada } from "../hooks/useOcupadas";
import type { RedKeel } from "../config/networks";
import { esEstable } from "../lib/precio";
import { num } from "../lib/formato";
import { ParMonedas } from "./Monedas";

/**
 * Elegir donde invertir.
 *
 * EL PROBLEMA QUE RESUELVE: la lista cruda repite el mismo activo una vez por comision. GME salia
 * cuatro veces (1%, 0,3%, 0,05%) y para elegir habia que saber que es un "fee tier". La gente no
 * piensa "quiero GME/USDG al 0,3%", piensa "quiero GME".
 *
 * Asi que se agrupa POR ACTIVO: una tarjeta por cosa en la que invertir, y dentro las comisiones
 * como opciones con su rendimiento al lado. De 8 tarjetas a 5, y la decision queda partida en dos
 * preguntas faciles en vez de una dificil.
 */
export function ElegirPool({
  pools, red, elegida, alElegir, deshabilitado, alAbrirVault,
}: {
  pools: Pool[];
  red: RedKeel;
  elegida: Pool | null;
  alElegir: (p: Pool) => void;
  deshabilitado?: boolean;
  alAbrirVault?: (v: Address) => void;
}) {
  const [busca, setBusca] = useState("");
  const ocupadas = useOcupadas(red, useMemo(() => pools.map((p) => p.pool), [pools]));

  /** El activo del par es el que NO es el estable. Y va primero: NVDA/USDG, no USDG/NVDA. */
  const activoDe = (p: Pool) =>
    esEstable(p.token0.symbol) && !esEstable(p.token1.symbol) ? p.token1 : p.token0;
  const estableDe = (p: Pool) =>
    esEstable(p.token0.symbol) && !esEstable(p.token1.symbol) ? p.token0 : p.token1;

  const grupos = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const mapa = new Map<string, { activo: string; estable: string; opciones: Pool[] }>();
    for (const p of pools) {
      const a = activoDe(p).symbol;
      const e = estableDe(p).symbol;
      if (q && !`${a} ${e} ${p.pair}`.toLowerCase().includes(q)) continue;
      // La clave es ACTIVO + ESTABLE, no solo el activo: un mismo activo puede tener pools contra
      // DOS referencias distintas (p.ej. UPUMP/WHYPE y UPUMP/USDT0), y agrupar solo por activo las
      // colapsaba en una tarjeta indistinguible. Cada par (activo, referencia) es su propia tarjeta.
      const clave = `${a}/${e}`;
      const g = mapa.get(clave) ?? { activo: a, estable: e, opciones: [] };
      g.opciones.push(p);
      mapa.set(clave, g);
    }
    // «Libre» = se puede crear aqui. Con la factory nueva lo estan TODAS: cada piscina es un vault
    // nuevo. Con las anteriores solo bloquea el vault vivo — el vacio se reutiliza.
    const libre = (p: Pool) => !ocupadas.data?.pools[p.pool.toLowerCase()]?.bloquea;
    return [...mapa.values()]
      .map((g) => ({
        ...g,
        // dentro de cada activo, la mejor primero — pero las ocupadas al final: no se pueden usar
        opciones: [...g.opciones].sort(
          (x, y) => Number(libre(y)) - Number(libre(x)) || (y.aprPct ?? 0) - (x.aprPct ?? 0)
        ),
      }))
      .sort((g, h) => (h.opciones[0]?.aprPct ?? 0) - (g.opciones[0]?.aprPct ?? 0));
  }, [pools, busca, ocupadas.data]);

  return (
    <>
      <div className="buscador">
        <input value={busca} onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar — GME, NVDA, ETH…" disabled={deshabilitado} aria-label="Buscar activo" />
        {busca && (
          <button className="enlace" style={{ padding: 0 }} onClick={() => setBusca("")}>limpiar</button>
        )}
      </div>

      {grupos.length === 0 && (
        <div className="vacio">
          <p>Nada que coincida con «{busca}».</p>
          <p className="detalle">
            En {red.etiqueta} solo se ofrecen las pools cuyo oráculo responde: el vault lo necesita
            para no operar con un precio manipulable.
          </p>
        </div>
      )}

      <div className="rejilla-activos">
        {grupos.map((g) => (
          <TarjetaActivo key={`${g.activo}/${g.estable}`} grupo={g} elegida={elegida} alElegir={alElegir}
            ocupadas={ocupadas.data?.pools ?? {}} deshabilitado={deshabilitado} alAbrirVault={alAbrirVault} />
        ))}
      </div>
    </>
  );
}

function TarjetaActivo({
  grupo, elegida, alElegir, ocupadas, deshabilitado, alAbrirVault,
}: {
  grupo: { activo: string; estable: string; opciones: Pool[] };
  elegida: Pool | null;
  alElegir: (p: Pool) => void;
  ocupadas: Record<string, Ocupada>;
  deshabilitado?: boolean;
  alAbrirVault?: (v: Address) => void;
}) {
  const enEsteGrupo = grupo.opciones.find((p) => p.pool === elegida?.pool);
  const libres = grupo.opciones.filter((p) => !ocupadas[p.pool.toLowerCase()]?.bloquea);
  const mostrada = enEsteGrupo ?? libres[0] ?? grupo.opciones[0]!;
  const vaultExistente = ocupadas[mostrada.pool.toLowerCase()];
  const vaultVivo = vaultExistente?.bloquea ? vaultExistente.viva : undefined;
  const activa = !!enEsteGrupo;

  /**
   * LA TARJETA ENTERA SE PULSA, y elige la mejor comision libre.
   *
   * Al reorganizar por activo se me quedaron pulsables solo los chips de comision, que son lo mas
   * pequeño de la tarjeta: pulsar el simbolo del activo —que es lo que hace cualquiera— no hacia
   * nada. Los chips siguen ahi para afinar, pero elegir "NVDA" tiene que funcionar sin saber que
   * es un fee tier.
   */
  const elegirMejor = () => {
    if (deshabilitado) return;
    if (libres[0]) alElegir(libres[0]);
    else if (vaultVivo) alAbrirVault?.(vaultVivo);
  };

  return (
    <article
      className={`activo ${activa ? "activo-sel" : ""} ${libres.length || vaultVivo ? "pulsable" : ""}`}
      onClick={elegirMejor}
      role="button"
      tabIndex={0}
      aria-pressed={activa}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); elegirMejor(); }
      }}
    >
      <header>
        {/* El logo antes que el nombre: en una rejilla de cinco tarjetas se encuentra «la de NVDA»
            por el verde, sin leer. Es la misma pareja de monedas que luego lleva la ficha del
            vault, para que la cosa elegida y la cosa abierta se reconozcan como la misma. */}
        <div className="ident-activo">
          <ParMonedas s0={grupo.activo} s1={grupo.estable} size={24} />
          <div>
            <span className="simbolo">{grupo.activo}</span>
            <span className="contra">/ {grupo.estable}</span>
          </div>
        </div>
        <div className="rendimiento">
          {/* 0 = el indexer aun no tiene ventana de volumen (o la pool esta tranquila): mostrar
              "0.0%" hacia pensar "pool muerta". Un guion es honesto y no asusta. */}
          <span className="v">{mostrada.aprPct ? `${mostrada.aprPct.toFixed(1)}%` : "—"}</span>
          <span className="k">anual</span>
        </div>
      </header>

      {/* Las comisiones, como opciones. Cada una lleva su rendimiento: es lo que de verdad cambia
          entre ellas, y sin verlo al lado la eleccion es a ciegas. */}
      <div className="comisiones">
        {grupo.opciones.map((p) => {
          const oc = ocupadas[p.pool.toLowerCase()];
          // Bloquea = la factory vieja y un vault vivo aqui. Solo entonces el chip lleva a abrirlo
          // en vez de elegirlo.
          const viva = oc?.bloquea ? oc.viva : undefined;
          // Y si NO bloquea pero ya hay algo, se dice igualmente: que ya tengas dos piscinas en esta
          // pool no te impide abrir la tercera, pero es lo primero que querrias saber antes.
          const repite = !oc?.bloquea && (oc?.vivas ?? 0) > 0;
          // La factory vieja con el vault vacio: la piscina se reutiliza, con su cuota y sus ajustes.
          const reusable = oc && !oc.bloquea && !oc.vivas;
          return (
            <button key={p.pool}
              className={`comi ${elegida?.pool === p.pool ? "sel" : ""} ${viva ? "ocupada" : ""}`}
              // No debe llegar a la tarjeta: si llegara, elegir el 0,3% a mano quedaria pisado
              // acto seguido por la "mejor libre" de la tarjeta.
              onClick={(e) => { e.stopPropagation(); viva ? alAbrirVault?.(viva) : alElegir(p); }}
              disabled={deshabilitado}
              title={
                viva ? "Ya tienes una piscina abierta en esta pool — pulsa para abrirla"
                : repite ? `Ya tienes ${oc!.vivas} piscina(s) abierta(s) aquí. Esta será otra, aparte, con su propio dinero`
                : reusable ? "Tienes aquí un vault vacío: se reutiliza, con su cuota y sus ajustes"
                : undefined
              }>
              <span className="fee">{p.fee / 10000}%</span>
              <span className="apr">{p.aprPct ? `${p.aprPct.toFixed(1)}%` : "—"}</span>
              {viva && <span className="marca">ya la tienes</span>}
              {repite && <span className="marca reusar">ya tienes {oc!.vivas}</span>}
              {reusable && <span className="marca reusar">se reutiliza</span>}
            </button>
          );
        })}
      </div>

      <dl>
        <div>
          <dt>Profundidad</dt>
          <dd>${num(mostrada.tvlUsd ?? 0, 0)}</dd>
        </div>
      </dl>

      {vaultVivo && !libres.length && (
        <p className="detalle malo">
          Ya tienes piscinas abiertas en todas las comisiones de {grupo.activo} con esta wallet. En
          esta red solo puede haber una por pool.
        </p>
      )}
    </article>
  );
}
